<template lang="pug">
.donation-dialog-backdrop(v-if="shouldShowDialog" @click="handleBackdropClick")
  .donation-dialog(@click.stop)
    .dialog-header
      h2.dialog-title Donate to {{ cause.causeName }}
      button.close-btn(@click="handleClose")
        img(src="@/assets/icons/close.svg" alt="Close")

    .cause-card
      img.cause-avatar(
        :src="cause.causeLogo || defaultCauseImage"
        :alt="cause.causeName"
      )
      .cause-info
        .cause-name {{ cause.causeName }}
        .cause-id EIN: {{ cause.causeId }}

    .donation-info(v-if="canDonate")
      .donation-label Donation Amount
      .donation-amount $1.00
      .donation-note(v-if="isOrgDonation") From {{ orgName }}'s Giving Wallet
      .donation-note(v-else) From Your Giving Wallet

    .eligibility-notice(v-if="eligibilityReason")
      span.notice-icon ⚠️
      span {{ eligibilityReason }}

    .eligibility-loading(v-if="isCheckingEligibility")
      span.spinner
      span Checking donation availability...

    .action-buttons
      button.action-btn.primary-btn(
        :disabled="isSubmitting || isCheckingEligibility || !canDonate"
        @click="handleSubmit"
      )
        span(v-if="!isSubmitting") Donate $1.00
        span.loading-text(v-else)
          span.spinner
          span Donating...

      button.action-btn.secondary-btn(@click="handleClose") Cancel
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import { makeRequest } from '@/graphs/index';
import { makeDonation, makeDonationForOrg } from '@/graphs/gql';
import { checkBenevityCauseEligibility } from './benevityEligibility';

interface Cause {
  id: string;
  causeId: string;
  causeName: string;
  causeLogo?: string;
  causeDescription?: string;
}

export default defineComponent({
  name: 'DialogDonateCause',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    cause: {
      type: Object as PropType<Cause>,
      required: true,
    },
    orgId: {
      type: String,
      required: false, // Made optional - if not provided, uses user's personal wallet
      default: null,
    },
    orgName: {
      type: String,
      default: 'Your',
    },
  },
  emits: ['update:modelValue', 'success'],
  setup(props, { emit }) {
    const store = useStore();

    const isSubmitting = ref(false);
    const isCheckingEligibility = ref(false);
    const canDonate = ref(true);
    const eligibilityReason = ref('');

    const defaultCauseImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e8f5f4" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" font-size="40" fill="%231fbeaf"%3E🏢%3C/text%3E%3C/svg%3E';

    const loadEligibility = async () => {
      const causeId = props.cause?.causeId;
      if (!causeId || !props.modelValue) {
        return;
      }

      isCheckingEligibility.value = true;
      canDonate.value = true;
      eligibilityReason.value = '';

      try {
        const token = store.getters['users/getToken'];
        const eligibility = await checkBenevityCauseEligibility(causeId, token);

        if (eligibility && !eligibility.isDonatable) {
          canDonate.value = false;
          eligibilityReason.value = eligibility.reason;
        }
      } finally {
        isCheckingEligibility.value = false;
      }
    };

    const shouldShowDialog = computed(() => {
      return props.modelValue && props.cause && props.cause.causeId;
    });
    
    // Determine if using organization wallet or personal wallet
    const isOrgDonation = computed(() => !!props.orgId);

    const handleSubmit = async () => {
      if (isSubmitting.value || !canDonate.value) {
        return;
      }

      isSubmitting.value = true;
      try {
        const token = store.getters['users/getToken'];

        console.log('========================================');
        console.log('MAKING DONATION');
        console.log('========================================');
        console.log('Wallet Type:', isOrgDonation.value ? 'ORGANIZATION' : 'PERSONAL USER');
        console.log('Amount: $1.00');
        console.log('Cause ID:', props.cause.causeId);
        console.log('Cause Name:', props.cause.causeName);
        
        let response;
        
        if (isOrgDonation.value) {
          // Donate from ORGANIZATION wallet
          console.log('Organization ID:', props.orgId);
          console.log('Organization Name:', props.orgName);
          console.log('Using mutation: makeDonationForOrg');
          
          const variables = {
            credits: 1,
            orgId: props.orgId,
            causeId: props.cause.causeId,
          };

          response = await makeRequest(
            'mutation',
            makeDonationForOrg,
            variables,
            { token },
          );
        } else {
          // Donate from PERSONAL USER wallet
          console.log('Using mutation: makeDonation (personal wallet)');
          
          const variables = {
            credits: 1,
            causeId: props.cause.causeId,
            recurring: 'false',
            notes: `Donation to ${props.cause.causeName}`,
          };

          response = await makeRequest(
            'mutation',
            makeDonation,
            variables,
            { token },
          );
        }

        if (response?.errors) {
          console.error('❌ DONATION FAILED:', response.errors[0]?.message);
          throw new Error(response.errors[0]?.message || 'Failed to make donation');
        }

        console.log('✓ DONATION SUCCESS');
        console.log('Response:', response);
        console.log('========================================');

        // Close this dialog immediately
        emit('update:modelValue', false);

        // Emit success event with cause info (parent will show success dialog)
        emit('success', {
          amount: 1,
          cause: props.cause,
        });

        isSubmitting.value = false;
      } catch (error: any) {
        console.error('❌ Donation error:', error);
        ElMessage.error(error.message || 'Donation failed. Please try again.');
        isSubmitting.value = false;
      }
    };

    const handleBackdropClick = () => {
      handleClose();
    };

    const handleClose = () => {
      emit('update:modelValue', false);

      setTimeout(() => {
        isSubmitting.value = false;
        canDonate.value = true;
        eligibilityReason.value = '';
      }, 300);
    };

    watch(
      () => [props.modelValue, props.cause?.causeId] as const,
      ([isOpen]) => {
        if (isOpen) {
          loadEligibility();
        }
      },
    );

    return {
      isSubmitting,
      isCheckingEligibility,
      canDonate,
      eligibilityReason,
      defaultCauseImage,
      shouldShowDialog,
      isOrgDonation,
      handleSubmit,
      handleBackdropClick,
      handleClose,
    };
  },
});
</script>

<style lang="scss" scoped>
.donation-dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.donation-dialog {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  max-width: 450px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
  position: relative;

  @media (min-width: 768px) {
    padding: 32px;
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
  padding-right: 1rem;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #e0e0e0;
  }

  img {
    width: 16px;
    height: 16px;
  }
}

.cause-card {
  padding: 16px;
  background-color: #f0fdfa;
  border-radius: 12px;
  border: 1px solid #ccfbf1;
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
}

.cause-avatar {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.cause-info {
  flex: 1;
  min-width: 0;
}

.cause-name {
  font-size: 1rem;
  font-weight: 600;
  color: #0f766e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

.cause-id {
  font-size: 0.875rem;
  color: #14b8a6;
}

.donation-info {
  text-align: center;
  padding: 24px;
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  border-radius: 12px;
  margin-bottom: 24px;
  color: white;
}

.donation-label {
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 8px;
}

.donation-amount {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.donation-note {
  font-size: 0.875rem;
  opacity: 0.9;
}

.eligibility-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  margin-bottom: 24px;
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.5;
}

.eligibility-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  margin-bottom: 24px;
  color: #6b7280;
  font-size: 0.875rem;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.primary-btn {
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  color: #ffffff;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
}

.secondary-btn {
  background-color: transparent;
  color: #14b8a6;
  border: 2px solid #14b8a6;

  &:hover {
    background-color: #f0fdfa;
  }
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
